import { windowWidth, windowHeight, asteroidRestitution } from './constants';
import { polygonMTV } from './util';


// Shared base for scenes that show asteroids (the game and the splash screen).
// Holds the asteroid collision-polygon data and the polygon intersection test
// so both can do precise, polygon-based asteroid-vs-asteroid collisions.
export class SpaceScene extends Phaser.Scene {
    protected asteroidPolygons: { [key: string]: Phaser.Geom.Point[] } = {};

    constructor(key: string) {
        super(key);
    }

    // Define asteroid polygons, sized to roughly fill each asteroid sprite
    // (asteroid0a/0b ~100x75, asteroid1 ~230x109) so the hitbox matches the
    // visible rock instead of just its (axis-aligned) bounding box.
    initAsteroidPolygons() {
        this.asteroidPolygons['asteroid0a'] = [
            new Phaser.Geom.Point(-44, -14),
            new Phaser.Geom.Point(-22, -32),
            new Phaser.Geom.Point(14, -33),
            new Phaser.Geom.Point(44, -10),
            new Phaser.Geom.Point(40, 20),
            new Phaser.Geom.Point(6, 33),
            new Phaser.Geom.Point(-34, 22)
        ];

        this.asteroidPolygons['asteroid0b'] = [
            new Phaser.Geom.Point(-43, -16),
            new Phaser.Geom.Point(-18, -31),
            new Phaser.Geom.Point(18, -31),
            new Phaser.Geom.Point(43, -8),
            new Phaser.Geom.Point(38, 20),
            new Phaser.Geom.Point(4, 32),
            new Phaser.Geom.Point(-36, 18)
        ];

        this.asteroidPolygons['asteroid1'] = [
            new Phaser.Geom.Point(-100, -22),
            new Phaser.Geom.Point(-45, -48),
            new Phaser.Geom.Point(40, -49),
            new Phaser.Geom.Point(100, -18),
            new Phaser.Geom.Point(92, 28),
            new Phaser.Geom.Point(25, 49),
            new Phaser.Geom.Point(-65, 38)
        ];
    }

    // Get asteroid polygon based on current position and rotation
    getAsteroidPolygon(asteroid: Phaser.Physics.Arcade.Image): Phaser.Geom.Polygon {
        const asteroidType = asteroid.getData('type') || asteroid.texture.key;

        if (!this.asteroidPolygons[asteroidType]) {
            // Fallback if polygon not defined
            const radius = asteroid.width / 2;
            const points = [];
            const sides = 8;

            for (let i = 0; i < sides; i++) {
                const angle = (i / sides) * Math.PI * 2;
                points.push(new Phaser.Geom.Point(
                    Math.cos(angle) * radius,
                    Math.sin(angle) * radius
                ));
            }

            this.asteroidPolygons[asteroidType] = points;
        }

        // Create a copy of the base polygon
        const basePoints = this.asteroidPolygons[asteroidType];
        const points: Phaser.Geom.Point[] = basePoints.map(p => new Phaser.Geom.Point(p.x, p.y));

        // Rotate and translate the polygon
        const rotation = asteroid.rotation;
        for (let i = 0; i < points.length; i++) {
            // First rotate around origin
            Phaser.Math.Rotate(points[i], rotation);
            // Then translate to asteroid's position
            points[i].x += asteroid.x;
            points[i].y += asteroid.y;
        }

        return new Phaser.Geom.Polygon(points);
    }

    // Robust polygon-polygon intersection test.
    // Returns true if any edge of one polygon crosses an edge of the other,
    // or if either polygon fully contains the other (the case that pure
    // edge-crossing tests miss, e.g. a small ship buried inside a big asteroid).
    polygonsIntersect(polygonA: Phaser.Geom.Polygon, polygonB: Phaser.Geom.Polygon): boolean {
        const pointsA = polygonA.points;
        const pointsB = polygonB.points;

        if (pointsA.length === 0 || pointsB.length === 0) {
            return false;
        }

        // 1. Edge vs edge: does any segment of A cross any segment of B?
        // Reuse two Line instances to avoid allocating inside the loops.
        const edgeA = new Phaser.Geom.Line();
        const edgeB = new Phaser.Geom.Line();

        for (let i = 0; i < pointsA.length; i++) {
            const a1 = pointsA[i];
            const a2 = pointsA[(i + 1) % pointsA.length];
            edgeA.setTo(a1.x, a1.y, a2.x, a2.y);

            for (let j = 0; j < pointsB.length; j++) {
                const b1 = pointsB[j];
                const b2 = pointsB[(j + 1) % pointsB.length];
                edgeB.setTo(b1.x, b1.y, b2.x, b2.y);

                if (Phaser.Geom.Intersects.LineToLine(edgeA, edgeB)) {
                    return true;
                }
            }
        }

        // 2. Containment: if no edges cross, one polygon may still be entirely
        // inside the other. Testing a single vertex of each is sufficient.
        if (Phaser.Geom.Polygon.Contains(polygonA, pointsB[0].x, pointsB[0].y)) {
            return true;
        }
        if (Phaser.Geom.Polygon.Contains(polygonB, pointsA[0].x, pointsA[0].y)) {
            return true;
        }

        return false;
    }

    // Overlap callback for two asteroids. We resolve the collision ourselves
    // using the real rock polygons (via SAT) rather than arcade's separation,
    // which resolves on the wrong axis and lets head-on rocks slide through.
    // SAT gives the contact normal and penetration depth, so we (1) push the
    // rocks apart by the penetration each frame - which separates them in a
    // single step, so each collision is resolved exactly once and momentum is
    // conserved - and (2) apply an elastic, mass-weighted impulse when they are
    // approaching.
    asteroidBounce(a: Phaser.Physics.Arcade.Image, b: Phaser.Physics.Arcade.Image) {
        const polyA = this.getAsteroidPolygon(a).points;
        const polyB = this.getAsteroidPolygon(b).points;
        const mtv = polygonMTV(polyA, polyB);
        if (!mtv) {
            return; // Bounding boxes overlapped but the rock shapes do not.
        }

        const ma = a.getData('mass') || 1;
        const mb = b.getData('mass') || 1;
        const invSum = 1 / ma + 1 / mb;

        // Positional correction: separate along the normal by the penetration
        // depth, split by mass (the heavier rock moves less).
        const corrA = mtv.depth * (1 / ma) / invSum;
        const corrB = mtv.depth * (1 / mb) / invSum;
        a.x -= mtv.nx * corrA;
        a.y -= mtv.ny * corrA;
        b.x += mtv.nx * corrB;
        b.y += mtv.ny * corrB;

        // Elastic impulse along the normal, only if the rocks are approaching.
        const vRel = (b.body.velocity.x - a.body.velocity.x) * mtv.nx
                   + (b.body.velocity.y - a.body.velocity.y) * mtv.ny;
        if (vRel < 0) {
            const j = -(1 + asteroidRestitution) * vRel / invSum;
            a.body.velocity.x -= (j / ma) * mtv.nx;
            a.body.velocity.y -= (j / ma) * mtv.ny;
            b.body.velocity.x += (j / mb) * mtv.nx;
            b.body.velocity.y += (j / mb) * mtv.ny;
        }
    }

    // Shared title-screen backdrop: the space sky plus a few asteroids drifting
    // and bouncing off each other (used by both the splash and credits screens).
    createBackdrop() {
        const w = windowWidth;
        const h = windowHeight;

        this.add.tileSprite(0, 0, w, h, 'farback').setOrigin(0, 0);

        this.initAsteroidPolygons();
        this.physics.world.setBounds(0, 0, w, h);
        const asteroids = this.physics.add.group({ classType: Phaser.Physics.Arcade.Image });
        const keys = ['asteroid0a', 'asteroid0b', 'asteroid1'];
        for (let i = 0; i < 7; i++) {
            const key = Phaser.Math.RND.pick(keys);
            const asteroid = asteroids.create(
                Phaser.Math.Between(100, w - 100),
                Phaser.Math.Between(100, h - 100),
                key) as Phaser.Physics.Arcade.Image;
            asteroid.setVelocity(Phaser.Math.Between(-90, 90), Phaser.Math.Between(-90, 90));
            asteroid.setAngularVelocity(Phaser.Math.Between(-40, 40));
            asteroid.setBounce(1, 1);
            asteroid.setCollideWorldBounds(true);
        }
        // Resolve asteroid-vs-asteroid with the real polygon shapes (asteroidBounce).
        this.physics.add.overlap(asteroids, asteroids, this.asteroidBounce, null, this);
    }

}
